import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { QuickAddForm } from '@/components/forms/QuickAddForm'
import { DataImport } from '@/components/forms/DataImport'

interface Summary {
  period: string
  income: { total: number; expected: number }
  expenses: {
    total: number
    expected: number
    byCategory: {
      id: number
      name: string
      icon: string | null
      color: string
      amount: number
      percentage: number
    }[]
  }
  netCashFlow: number
  savingsRate: number
  recurring: {
    expectedIncome: number
    expectedExpenses: number
    expectedNetCashFlow: number
    expectedSavingsRate: number
  }
  netWorth: { total: number; assets: number; debts: number }
  primaryGoal: {
    id: number
    name: string
    target_amount: number
    current_amount: number
    progress: number
    remaining: number
  } | null
}

interface SummaryCardProps {
  title: string
  value: string
  expectedValue?: string
  trend: 'up' | 'down' | 'neutral'
  icon: string
  color?: string
  expectedLabel?: string
}

function SummaryCard({
  title,
  value,
  expectedValue,
  icon,
  color = 'text-charcoal',
  expectedLabel = 'Expected'
}: SummaryCardProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-5 border border-stone/10">
      <div className="flex items-center justify-between">
        <span className="text-2xl">{icon}</span>
      </div>
      <div className="mt-3">
        <p className="text-sm text-sage">{title}</p>
        <p className={`text-2xl font-bold tabular-nums mt-1 ${color}`}>
          {value}
        </p>
        {expectedValue && (
          <p className="text-xs text-stone mt-1 tabular-nums">
            {expectedLabel}: {expectedValue}
          </p>
        )}
      </div>
    </div>
  )
}

export function Overview() {
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false)
  const [isImportOpen, setIsImportOpen] = useState(false)
  const [summary, setSummary] = useState<Summary | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchSummary = useCallback(async () => {
    try {
      const response = await fetch('/api/summary?period=month')
      if (response.ok) {
        const data = (await response.json()) as Summary
        setSummary(data)
      }
    } catch (err) {
      console.error('Error fetching summary:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  function handleDataChange() {
    fetchSummary()
  }

  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount)
  }

  function getProgressColor(progress: number): string {
    if (progress < 25) return 'from-danger to-warning'
    if (progress < 50) return 'from-warning to-sky'
    if (progress < 75) return 'from-sky to-sage'
    return 'from-sage to-success'
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-forest-dark">Overview</h1>
          <p className="text-sage mt-1">Your financial summary at a glance</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsImportOpen(true)}
            className="px-4 py-2 border border-forest text-forest rounded-lg text-sm hover:bg-forest/5 transition-colors"
          >
            Import Excel
          </button>
          <button
            onClick={() => setIsQuickAddOpen(true)}
            className="px-4 py-2 bg-forest text-white rounded-lg text-sm hover:bg-forest-dark transition-colors"
          >
            + Quick Add
          </button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12 text-sage">Loading summary...</div>
      )}

      {!isLoading && summary && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryCard
              title="Total Income"
              value={formatCurrency(summary.income.total)}
              expectedValue={summary.recurring?.expectedIncome > 0 ? formatCurrency(summary.recurring.expectedIncome) : undefined}
              trend={summary.income.total > 0 ? 'up' : 'neutral'}
              icon="💰"
              color="text-success"
            />
            <SummaryCard
              title="Total Expenses"
              value={formatCurrency(summary.expenses.total)}
              expectedValue={summary.recurring?.expectedExpenses > 0 ? formatCurrency(summary.recurring.expectedExpenses) : undefined}
              trend={summary.expenses.total > 0 ? 'down' : 'neutral'}
              icon="💸"
              color="text-danger"
            />
            <SummaryCard
              title="Net Cash Flow"
              value={formatCurrency(summary.netCashFlow)}
              expectedValue={summary.recurring ? formatCurrency(summary.recurring.expectedNetCashFlow) : undefined}
              trend={
                summary.netCashFlow > 0
                  ? 'up'
                  : summary.netCashFlow < 0
                  ? 'down'
                  : 'neutral'
              }
              icon="📊"
              color={summary.netCashFlow >= 0 ? 'text-success' : 'text-danger'}
            />
            <SummaryCard
              title="Savings Rate"
              value={`${summary.savingsRate}%`}
              expectedValue={summary.recurring?.expectedSavingsRate !== undefined ? `${summary.recurring.expectedSavingsRate}%` : undefined}
              trend={summary.savingsRate > 20 ? 'up' : 'neutral'}
              icon="🎯"
              color={summary.savingsRate > 0 ? 'text-success' : 'text-stone'}
            />
          </div>

          {/* Net Worth Widget */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-charcoal">Net Worth</h2>
              <span className="text-sm text-sage">
                Total Assets - Total Debts
              </span>
            </div>
            <div className="text-center py-8">
              <p
                className={`text-4xl font-bold tabular-nums ${
                  summary.netWorth.total >= 0 ? 'text-forest-dark' : 'text-danger'
                }`}
              >
                {formatCurrency(summary.netWorth.total)}
              </p>
              {summary.netWorth.assets === 0 && summary.netWorth.debts === 0 ? (
                <p className="text-sage mt-2">
                  Add assets and debts to see your net worth
                </p>
              ) : (
                <div className="flex justify-center gap-8 mt-4 text-sm">
                  <div>
                    <span className="text-sage">Assets: </span>
                    <span className="font-medium text-success tabular-nums">
                      {formatCurrency(summary.netWorth.assets)}
                    </span>
                  </div>
                  <div>
                    <span className="text-sage">Debts: </span>
                    <span className="font-medium text-danger tabular-nums">
                      {formatCurrency(summary.netWorth.debts)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Top Expense Categories */}
          {summary.expenses.byCategory.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-charcoal">
                  Top Expenses This Month
                </h2>
                <Link to="/expenses" className="text-sm text-sage hover:text-forest">
                  View all →
                </Link>
              </div>
              <div className="space-y-3">
                {summary.expenses.byCategory.slice(0, 5).map((cat) => (
                  <div key={cat.id} className="flex items-center gap-3">
                    <span className="text-xl">{cat.icon}</span>
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-charcoal">
                          {cat.name}
                        </span>
                        <span className="tabular-nums text-charcoal">
                          {formatCurrency(cat.amount)}
                        </span>
                      </div>
                      <div className="h-2 bg-cream rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${cat.percentage}%`,
                            backgroundColor: cat.color
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-sage w-10 text-right">
                      {cat.percentage}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Goal Progress */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-charcoal">
                Goal Progress
              </h2>
              <Link to="/goals" className="text-sm text-sage hover:text-forest">
                Manage goals →
              </Link>
            </div>
            {summary.primaryGoal ? (
              <div>
                <div className="flex justify-between mb-2">
                  <span className="font-medium text-charcoal">
                    {summary.primaryGoal.name}
                  </span>
                  <span className="text-sm font-bold text-forest">
                    {summary.primaryGoal.progress}%
                  </span>
                </div>
                <div className="flex justify-between text-sm text-sage mb-2">
                  <span className="tabular-nums">
                    {formatCurrency(summary.primaryGoal.current_amount)}
                  </span>
                  <span className="tabular-nums">
                    {formatCurrency(summary.primaryGoal.target_amount)}
                  </span>
                </div>
                <div className="h-4 bg-cream rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${getProgressColor(summary.primaryGoal.progress)} rounded-full transition-all`}
                    style={{ width: `${summary.primaryGoal.progress}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-stone">
                  <span>25%</span>
                  <span>50%</span>
                  <span>75%</span>
                  <span>100%</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sage">No goals set yet</p>
                <Link
                  to="/goals"
                  className="mt-4 inline-block px-4 py-2 bg-forest text-white rounded-lg text-sm hover:bg-forest-dark transition-colors"
                >
                  Create Your First Goal
                </Link>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modals */}
      <QuickAddForm
        isOpen={isQuickAddOpen}
        onClose={() => setIsQuickAddOpen(false)}
        onSuccess={handleDataChange}
      />
      <DataImport
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onSuccess={handleDataChange}
      />
    </div>
  )
}
